/**
 * NavigatorX Support Service
 * 
 * Enterprise-grade customer support management:
 * - Ticket management with priorities and categories
 * - In-app feedback collection
 * - Feature request tracking
 * - Bug report management
 * - Quick actions and macros
 * - Response time tracking
 * 
 * @author Claude AI for TGE.LA
 * @version 1.0.1 - Audit fixes applied
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  addDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  increment
} from 'firebase/firestore';
import { navDb } from '../firebase/client';
import { logAdminAction } from './navigatorXAuditService';
import { NAV_USERS_COLLECTION } from './navigatorXService';

// ============================================================================
// COLLECTION CONSTANTS
// ============================================================================

const TICKETS_COLLECTION = 'nav_support_tickets';
const FEEDBACK_COLLECTION = 'nav_feedback';
const FEATURE_REQUESTS_COLLECTION = 'nav_feature_requests';
const BUG_REPORTS_COLLECTION = 'nav_bug_reports';
const SUPPORT_MACROS_COLLECTION = 'nav_support_macros';

// Validation constants
const MAX_SUBJECT_LENGTH = 200;
const MAX_DESCRIPTION_LENGTH = 5000;
const MAX_MESSAGE_LENGTH = 2000;

// ============================================================================
// VALIDATION & SANITIZATION HELPERS
// ============================================================================

/**
 * Validate admin ID - throws if invalid
 */
function validateAdminId(adminId: string): void {
  if (!adminId || adminId === 'unknown' || adminId.trim() === '') {
    throw new Error('Valid admin authentication required for this operation');
  }
}

/**
 * Sanitize string input to prevent XSS
 */
function sanitizeHtml(input: string): string {
  if (!input) return '';
  return input
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;')
    .trim();
}

/**
 * Validate string length
 */
function validateLength(input: string, maxLength: number, fieldName: string): void {
  if (input && input.length > maxLength) {
    throw new Error(`${fieldName} exceeds maximum length of ${maxLength} characters`);
  }
}

/**
 * Validate required string field
 */
function validateRequired(input: string | undefined, fieldName: string): void {
  if (!input || input.trim() === '') {
    throw new Error(`${fieldName} is required`);
  }
}

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface SupportTicket {
  id?: string;
  userId: string;
  userEmail?: string;
  userName?: string;
  
  subject: string;
  description: string;
  category: 'billing' | 'technical' | 'account' | 'feature' | 'other';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'open' | 'in_progress' | 'waiting_user' | 'resolved' | 'closed';
  
  assignedTo?: string;
  tags: string[];
  
  attachments: { name: string; url: string; type: string }[];
  messages: TicketMessage[];
  
  createdAt: Timestamp;
  updatedAt: Timestamp;
  firstResponseAt?: Timestamp;
  resolvedAt?: Timestamp;
  closedAt?: Timestamp;
  
  satisfactionRating?: 1 | 2 | 3 | 4 | 5;
  satisfactionFeedback?: string;
  internalNotes: string[];
}

export interface TicketMessage {
  id: string;
  sender: 'user' | 'agent';
  senderId: string;
  senderName?: string;
  content: string;
  timestamp: Timestamp;
  isInternal: boolean;
}

export interface UserFeedback {
  id?: string;
  userId: string;
  type: 'general' | 'suggestion' | 'complaint' | 'praise';
  content: string;
  rating?: 1 | 2 | 3 | 4 | 5;
  source: 'in_app' | 'email' | 'app_store' | 'play_store';
  screenName?: string;
  deviceInfo?: { platform: string; version: string; model: string };
  createdAt: Timestamp;
  status: 'new' | 'reviewed' | 'actioned' | 'archived';
  reviewedBy?: string;
  reviewedAt?: Timestamp;
  notes?: string;
}

export interface FeatureRequest {
  id?: string;
  userId: string;
  title: string;
  description: string;
  category: 'navigation' | 'search' | 'ui' | 'offline' | 'integration' | 'other';
  status: 'submitted' | 'under_review' | 'planned' | 'in_progress' | 'completed' | 'declined';
  priority: 'low' | 'medium' | 'high';
  votes: number;
  voterIds: string[];
  createdAt: Timestamp;
  updatedAt: Timestamp;
  targetRelease?: string;
  adminResponse?: string;
  adminRespondedAt?: Timestamp;
}

export interface BugReport {
  id?: string;
  userId: string;
  title: string;
  description: string;
  stepsToReproduce: string;
  expectedBehavior: string;
  actualBehavior: string;
  severity: 'minor' | 'major' | 'critical' | 'blocker';
  status: 'open' | 'confirmed' | 'in_progress' | 'fixed' | 'cannot_reproduce' | 'wont_fix';
  deviceInfo: { platform: string; version: string; model: string; osVersion: string };
  attachments: { name: string; url: string; type: string }[];
  createdAt: Timestamp;
  updatedAt: Timestamp;
  assignedTo?: string;
  fixedInVersion?: string;
}

export interface SupportMacro {
  id?: string;
  name: string;
  description: string;
  category: string;
  actions: MacroAction[];
  createdBy: string;
  createdAt: Timestamp;
  usageCount: number;
}

export interface MacroAction {
  type: 'send_message' | 'change_status' | 'add_tag' | 'assign' | 'extend_trial' | 'grant_credit';
  params: Record<string, any>;
}

export interface SupportMetrics {
  openTickets: number;
  inProgressTickets: number;
  avgFirstResponseMinutes: number;
  avgResolutionMinutes: number;
  avgSatisfaction: number;
  ticketsByCategory: Record<string, number>;
  ticketsByPriority: Record<string, number>;
  resolvedToday: number;
  newToday: number;
}

// ============================================================================
// TICKET MANAGEMENT
// ============================================================================

export async function createTicket(
  ticket: Omit<SupportTicket, 'id' | 'createdAt' | 'updatedAt' | 'messages'>
): Promise<string> {
  // Validate required fields
  validateRequired(ticket.userId, 'User ID');
  validateRequired(ticket.subject, 'Subject');
  validateRequired(ticket.description, 'Description');
  
  // Validate lengths
  validateLength(ticket.subject, MAX_SUBJECT_LENGTH, 'Subject');
  validateLength(ticket.description, MAX_DESCRIPTION_LENGTH, 'Description');
  
  // Sanitize user input
  const sanitizedTicket = {
    ...ticket,
    subject: sanitizeHtml(ticket.subject),
    description: sanitizeHtml(ticket.description),
    userEmail: ticket.userEmail ? sanitizeHtml(ticket.userEmail) : undefined,
    userName: ticket.userName ? sanitizeHtml(ticket.userName) : undefined,
    tags: ticket.tags?.map(t => sanitizeHtml(t)) || [],
  };
  
  try {
    const docRef = await addDoc(collection(navDb, TICKETS_COLLECTION), {
      ...sanitizedTicket,
      messages: [],
      internalNotes: [],
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    return docRef.id;
  } catch (error) {
    console.error('Error creating ticket:', error);
    throw new Error('Failed to create support ticket');
  }
}

export async function getTickets(filters?: {
  status?: SupportTicket['status'];
  category?: SupportTicket['category'];
  priority?: SupportTicket['priority'];
  assignedTo?: string;
  userId?: string;
}): Promise<SupportTicket[]> {
  try {
    const q = query(collection(navDb, TICKETS_COLLECTION), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    
    let tickets = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as SupportTicket[];
    
    // Apply filters in memory (Firestore compound query limitations)
    if (filters?.status) tickets = tickets.filter(t => t.status === filters.status);
    if (filters?.category) tickets = tickets.filter(t => t.category === filters.category);
    if (filters?.priority) tickets = tickets.filter(t => t.priority === filters.priority);
    if (filters?.assignedTo) tickets = tickets.filter(t => t.assignedTo === filters.assignedTo);
    if (filters?.userId) tickets = tickets.filter(t => t.userId === filters.userId);
    
    return tickets;
  } catch (error) {
    console.error('Error fetching tickets:', error);
    throw new Error('Failed to fetch support tickets');
  }
}

export async function getTicket(ticketId: string): Promise<SupportTicket | null> {
  if (!ticketId) return null;
  
  try {
    const docSnap = await getDoc(doc(navDb, TICKETS_COLLECTION, ticketId));
    if (!docSnap.exists()) return null;
    return { id: docSnap.id, ...docSnap.data() } as SupportTicket;
  } catch (error) {
    console.error('Error fetching ticket:', error);
    return null;
  }
}

export async function updateTicket(
  ticketId: string,
  updates: Partial<SupportTicket>,
  adminId: string
): Promise<void> {
  validateAdminId(adminId);
  validateRequired(ticketId, 'Ticket ID');
  
  // Sanitize any text fields in updates
  const sanitizedUpdates: Partial<SupportTicket> = { ...updates };
  if (updates.subject) sanitizedUpdates.subject = sanitizeHtml(updates.subject);
  if (updates.description) sanitizedUpdates.description = sanitizeHtml(updates.description);
  
  try {
    await updateDoc(doc(navDb, TICKETS_COLLECTION, ticketId), {
      ...sanitizedUpdates,
      updatedAt: Timestamp.now()
    });
    await logAdminAction(adminId, 'UPDATE_TICKET', ticketId, { updates: Object.keys(updates) });
  } catch (error) {
    console.error('Error updating ticket:', error);
    throw new Error('Failed to update ticket');
  }
}

export async function addTicketMessage(
  ticketId: string,
  message: Omit<TicketMessage, 'id' | 'timestamp'>,
  adminId: string
): Promise<void> {
  validateAdminId(adminId);
  validateRequired(ticketId, 'Ticket ID');
  validateRequired(message.content, 'Message content');
  validateLength(message.content, MAX_MESSAGE_LENGTH, 'Message');
  
  const ticket = await getTicket(ticketId);
  if (!ticket) throw new Error('Ticket not found');
  
  const newMessage: TicketMessage = {
    ...message,
    content: sanitizeHtml(message.content),
    senderName: message.senderName ? sanitizeHtml(message.senderName) : undefined,
    id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    timestamp: Timestamp.now()
  };
  
  const updates: Partial<SupportTicket> = {
    messages: [...ticket.messages, newMessage],
    updatedAt: Timestamp.now()
  };
  
  // Track first response time
  if (message.sender === 'agent' && !ticket.firstResponseAt) {
    updates.firstResponseAt = Timestamp.now();
  }
  
  try {
    await updateDoc(doc(navDb, TICKETS_COLLECTION, ticketId), updates);
    await logAdminAction(adminId, 'ADD_TICKET_MESSAGE', ticketId, { messageId: newMessage.id });
  } catch (error) {
    console.error('Error adding ticket message:', error);
    throw new Error('Failed to add message');
  }
}

export async function changeTicketStatus(
  ticketId: string,
  status: SupportTicket['status'],
  adminId: string
): Promise<void> {
  validateAdminId(adminId);
  validateRequired(ticketId, 'Ticket ID');
  
  const validStatuses: SupportTicket['status'][] = ['open', 'in_progress', 'waiting_user', 'resolved', 'closed'];
  if (!validStatuses.includes(status)) {
    throw new Error('Invalid ticket status');
  }
  
  const updates: Partial<SupportTicket> = { status, updatedAt: Timestamp.now() };
  if (status === 'resolved') updates.resolvedAt = Timestamp.now();
  if (status === 'closed') updates.closedAt = Timestamp.now();
  
  try {
    await updateDoc(doc(navDb, TICKETS_COLLECTION, ticketId), updates);
    await logAdminAction(adminId, 'CHANGE_TICKET_STATUS', ticketId, { status });
  } catch (error) {
    console.error('Error changing ticket status:', error);
    throw new Error('Failed to change ticket status');
  }
}

export async function assignTicket(ticketId: string, assignedTo: string, adminId: string): Promise<void> {
  validateAdminId(adminId);
  validateRequired(ticketId, 'Ticket ID');
  validateRequired(assignedTo, 'Assignee ID');
  
  try {
    await updateDoc(doc(navDb, TICKETS_COLLECTION, ticketId), {
      assignedTo,
      status: 'in_progress',
      updatedAt: Timestamp.now()
    });
    await logAdminAction(adminId, 'ASSIGN_TICKET', ticketId, { assignedTo });
  } catch (error) {
    console.error('Error assigning ticket:', error);
    throw new Error('Failed to assign ticket');
  }
}

// ============================================================================
// FEEDBACK MANAGEMENT
// ============================================================================

export async function getFeedback(filters?: {
  status?: UserFeedback['status'];
  type?: UserFeedback['type'];
  source?: UserFeedback['source'];
}): Promise<UserFeedback[]> {
  try {
    const q = query(collection(navDb, FEEDBACK_COLLECTION), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    
    let feedback = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as UserFeedback[];
    
    if (filters?.status) feedback = feedback.filter(f => f.status === filters.status);
    if (filters?.type) feedback = feedback.filter(f => f.type === filters.type);
    if (filters?.source) feedback = feedback.filter(f => f.source === filters.source);
    
    return feedback;
  } catch (error) {
    console.error('Error fetching feedback:', error);
    throw new Error('Failed to fetch feedback');
  }
}

export async function reviewFeedback(
  feedbackId: string,
  adminId: string,
  notes?: string,
  status: UserFeedback['status'] = 'reviewed'
): Promise<void> {
  validateAdminId(adminId);
  validateRequired(feedbackId, 'Feedback ID');
  
  try {
    await updateDoc(doc(navDb, FEEDBACK_COLLECTION, feedbackId), {
      status,
      reviewedBy: adminId,
      reviewedAt: Timestamp.now(),
      notes: notes ? sanitizeHtml(notes) : null
    });
    await logAdminAction(adminId, 'REVIEW_FEEDBACK', feedbackId, { status });
  } catch (error) {
    console.error('Error reviewing feedback:', error);
    throw new Error('Failed to review feedback');
  }
}

// ============================================================================
// FEATURE REQUESTS
// ============================================================================

export async function getFeatureRequests(filters?: {
  status?: FeatureRequest['status'];
  category?: FeatureRequest['category'];
}): Promise<FeatureRequest[]> {
  try {
    const q = query(collection(navDb, FEATURE_REQUESTS_COLLECTION), orderBy('votes', 'desc'));
    const snapshot = await getDocs(q);
    
    let requests = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as FeatureRequest[];
    
    if (filters?.status) requests = requests.filter(r => r.status === filters.status);
    if (filters?.category) requests = requests.filter(r => r.category === filters.category);
    
    return requests;
  } catch (error) {
    console.error('Error fetching feature requests:', error);
    throw new Error('Failed to fetch feature requests');
  }
}

export async function updateFeatureRequestStatus(
  requestId: string,
  status: FeatureRequest['status'],
  adminId: string,
  response?: string,
  targetRelease?: string
): Promise<void> {
  validateAdminId(adminId);
  validateRequired(requestId, 'Request ID');
  
  try {
    await updateDoc(doc(navDb, FEATURE_REQUESTS_COLLECTION, requestId), {
      status,
      adminResponse: response ? sanitizeHtml(response) : null,
      adminRespondedAt: response ? Timestamp.now() : null,
      targetRelease: targetRelease ? sanitizeHtml(targetRelease) : null,
      updatedAt: Timestamp.now()
    });
    await logAdminAction(adminId, 'UPDATE_FEATURE_REQUEST', requestId, { status });
  } catch (error) {
    console.error('Error updating feature request:', error);
    throw new Error('Failed to update feature request');
  }
}

// ============================================================================
// BUG REPORTS
// ============================================================================

export async function getBugReports(filters?: {
  status?: BugReport['status'];
  severity?: BugReport['severity'];
}): Promise<BugReport[]> {
  try {
    const q = query(collection(navDb, BUG_REPORTS_COLLECTION), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    
    let reports = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as BugReport[];
    
    if (filters?.status) reports = reports.filter(r => r.status === filters.status);
    if (filters?.severity) reports = reports.filter(r => r.severity === filters.severity);
    
    return reports;
  } catch (error) {
    console.error('Error fetching bug reports:', error);
    throw new Error('Failed to fetch bug reports');
  }
}

export async function updateBugReportStatus(
  reportId: string,
  status: BugReport['status'],
  adminId: string,
  fixedInVersion?: string
): Promise<void> {
  validateAdminId(adminId);
  validateRequired(reportId, 'Report ID');
  
  try {
    await updateDoc(doc(navDb, BUG_REPORTS_COLLECTION, reportId), {
      status,
      fixedInVersion: fixedInVersion ? sanitizeHtml(fixedInVersion) : null,
      updatedAt: Timestamp.now()
    });
    await logAdminAction(adminId, 'UPDATE_BUG_REPORT', reportId, { status, fixedInVersion });
  } catch (error) {
    console.error('Error updating bug report:', error);
    throw new Error('Failed to update bug report');
  }
}

// ============================================================================
// SUPPORT MACROS
// ============================================================================

export async function getMacros(): Promise<SupportMacro[]> {
  try {
    const q = query(collection(navDb, SUPPORT_MACROS_COLLECTION), orderBy('usageCount', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as SupportMacro[];
  } catch (error) {
    console.error('Error fetching macros:', error);
    throw new Error('Failed to fetch macros');
  }
}

export async function createMacro(
  macro: Omit<SupportMacro, 'id' | 'createdAt' | 'usageCount'>,
  adminId: string
): Promise<string> {
  validateAdminId(adminId);
  validateRequired(macro.name, 'Macro name');
  
  try {
    const docRef = await addDoc(collection(navDb, SUPPORT_MACROS_COLLECTION), {
      ...macro,
      name: sanitizeHtml(macro.name),
      description: sanitizeHtml(macro.description || ''),
      createdBy: adminId,
      createdAt: Timestamp.now(),
      usageCount: 0
    });
    return docRef.id;
  } catch (error) {
    console.error('Error creating macro:', error);
    throw new Error('Failed to create macro');
  }
}

export async function executeMacro(
  macroId: string,
  ticketId: string,
  adminId: string
): Promise<void> {
  validateAdminId(adminId);
  validateRequired(macroId, 'Macro ID');
  validateRequired(ticketId, 'Ticket ID');
  
  const macroDoc = await getDoc(doc(navDb, SUPPORT_MACROS_COLLECTION, macroId));
  if (!macroDoc.exists()) throw new Error('Macro not found');
  
  const macro = macroDoc.data() as SupportMacro;
  const ticket = await getTicket(ticketId);
  if (!ticket) throw new Error('Ticket not found');
  
  try {
    for (const action of macro.actions) {
      switch (action.type) {
        case 'send_message':
          if (action.params.message) {
            await addTicketMessage(ticketId, {
              sender: 'agent',
              senderId: adminId,
              content: action.params.message,
              isInternal: false
            }, adminId);
          }
          break;
        case 'change_status':
          if (action.params.status) {
            await changeTicketStatus(ticketId, action.params.status, adminId);
          }
          break;
        case 'add_tag':
          if (action.params.tag) {
            await updateTicket(ticketId, {
              tags: [...(ticket.tags || []), sanitizeHtml(action.params.tag)]
            }, adminId);
          }
          break;
      }
    }
    
    await updateDoc(doc(navDb, SUPPORT_MACROS_COLLECTION, macroId), {
      usageCount: increment(1)
    });
    
    await logAdminAction(adminId, 'EXECUTE_MACRO', ticketId, { macroId, macroName: macro.name });
  } catch (error) {
    console.error('Error executing macro:', error);
    throw new Error('Failed to execute macro');
  }
}

// ============================================================================
// METRICS
// ============================================================================

export async function getSupportMetrics(): Promise<SupportMetrics> {
  try {
    const tickets = await getTickets();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const openTickets = tickets.filter(t => t.status === 'open').length;
    const inProgressTickets = tickets.filter(t => t.status === 'in_progress').length;
    
    // Calculate average first response time
    const ticketsWithResponse = tickets.filter(t => t.firstResponseAt && t.createdAt);
    let avgFirstResponseMinutes = 0;
    if (ticketsWithResponse.length > 0) {
      const totalMinutes = ticketsWithResponse.reduce((sum, t) => {
        try {
          const created = t.createdAt.toDate().getTime();
          const responded = t.firstResponseAt!.toDate().getTime();
          return sum + Math.max(0, (responded - created) / 60000);
        } catch {
          return sum;
        }
      }, 0);
      avgFirstResponseMinutes = Math.round(totalMinutes / ticketsWithResponse.length);
    }
    
    // Calculate average resolution time
    const resolvedTickets = tickets.filter(t => t.resolvedAt && t.createdAt);
    let avgResolutionMinutes = 0;
    if (resolvedTickets.length > 0) {
      const totalMinutes = resolvedTickets.reduce((sum, t) => {
        try {
          const created = t.createdAt.toDate().getTime();
          const resolved = t.resolvedAt!.toDate().getTime();
          return sum + Math.max(0, (resolved - created) / 60000);
        } catch {
          return sum;
        }
      }, 0);
      avgResolutionMinutes = Math.round(totalMinutes / resolvedTickets.length);
    }
    
    // Calculate average satisfaction
    const ratedTickets = tickets.filter(t => t.satisfactionRating);
    const avgSatisfaction = ratedTickets.length > 0
      ? ratedTickets.reduce((sum, t) => sum + (t.satisfactionRating || 0), 0) / ratedTickets.length
      : 0;
    
    // Count by category
    const ticketsByCategory: Record<string, number> = {};
    tickets.forEach(t => {
      if (t.category) {
        ticketsByCategory[t.category] = (ticketsByCategory[t.category] || 0) + 1;
      }
    });
    
    // Count by priority
    const ticketsByPriority: Record<string, number> = {};
    tickets.forEach(t => {
      if (t.priority) {
        ticketsByPriority[t.priority] = (ticketsByPriority[t.priority] || 0) + 1;
      }
    });
    
    // Today's stats
    const resolvedToday = tickets.filter(t => {
      try {
        return t.resolvedAt && t.resolvedAt.toDate() >= today;
      } catch {
        return false;
      }
    }).length;
    
    const newToday = tickets.filter(t => {
      try {
        return t.createdAt && t.createdAt.toDate() >= today;
      } catch {
        return false;
      }
    }).length;
    
    return {
      openTickets,
      inProgressTickets,
      avgFirstResponseMinutes,
      avgResolutionMinutes,
      avgSatisfaction: Math.round(avgSatisfaction * 10) / 10,
      ticketsByCategory,
      ticketsByPriority,
      resolvedToday,
      newToday
    };
  } catch (error) {
    console.error('Error calculating support metrics:', error);
    // Return safe defaults
    return {
      openTickets: 0,
      inProgressTickets: 0,
      avgFirstResponseMinutes: 0,
      avgResolutionMinutes: 0,
      avgSatisfaction: 0,
      ticketsByCategory: {},
      ticketsByPriority: {},
      resolvedToday: 0,
      newToday: 0
    };
  }
}

// ============================================================================
// QUICK USER LOOKUP
// ============================================================================

export async function lookupUserForSupport(userId: string): Promise<{
  user: any;
  tickets: SupportTicket[];
  feedback: UserFeedback[];
} | null> {
  if (!userId || userId.trim() === '') {
    return null;
  }
  
  try {
    const userDoc = await getDoc(doc(navDb, NAV_USERS_COLLECTION, userId));
    if (!userDoc.exists()) return null;
    
    const [tickets, feedback] = await Promise.all([
      getTickets({ userId }),
      getFeedback().then(f => f.filter(fb => fb.userId === userId))
    ]);
    
    return {
      user: { id: userDoc.id, ...userDoc.data() },
      tickets,
      feedback
    };
  } catch (error) {
    console.error('Error looking up user for support:', error);
    return null;
  }
}
